import PropTypes from 'prop-types'
import { createContext, useState, useEffect, useRef, useCallback } from 'react'
import Spinner from 'react-bootstrap/Spinner'
import { ID_PERFIL_ADMIN_EBP, ID_PERFIL_ADMIN_EMPRESA } from '../../constants/perfiles.js'
import { cerrarSesion } from '../../helpers/cerrarSesion'
import { decodeToken } from '../../helpers/decodeToken'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import {
  setTokenGetter,
  setOnTokenRenovado,
  setOnSesionExpirada,
  renovarToken
} from '../servicios/httpService'
import * as loginService from '../servicios/loginService'
import * as usuarioService from '../servicios/usuarioService'

const MS_POR_SEGUNDO = 1000
const MS_ANTES_DE_RENOVAR = 30000
const TIEMPO_GRACIA_CIERRE_MS = 30000
const VENTANA_INACTIVIDAD_DEFAULT_MS = 300000
const VENTANA_INACTIVIDAD_MS =
  Number(import.meta.env.VITE_EXPIRATION_TIME) || VENTANA_INACTIVIDAD_DEFAULT_MS

const getTokenExpireMS = (jwt) => {
  const payload = decodeToken(jwt)
  if (!payload || !Number.isFinite(payload.exp)) return null
  return payload.exp * MS_POR_SEGUNDO - Date.now() - MS_ANTES_DE_RENOVAR
}

/**
 * Indica si un JWT ya venció (o no es decodificable). Se usa en el arranque para
 * no rehidratar/renovar una sesión muerta con un refreshToken viejo.
 *
 * @param {string} jwt
 * @returns {boolean}
 */
const tokenVencido = (jwt) => {
  const payload = decodeToken(jwt)
  if (!payload || !Number.isFinite(payload.exp)) return true
  return payload.exp * MS_POR_SEGUNDO <= Date.now()
}

export const LoginContext = createContext()

export const LoginProvider = (props) => {
  const [token, setToken] = useState('')
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [cargandoUsuario, setCargandoUsuario] = useState(true)
  const [mostrarAvisoSesion, setMostrarAvisoSesion] = useState(false)
  const timerRef = useRef(null)
  const graciaRef = useRef(null)
  const renovarRef = useRef(null)
  const avisarInactividadRef = useRef(null)
  const ultimaActividadRef = useRef(Date.now())

  const [loginDataContext, setLoginDataContext] = useState(() => ({
    nombre: '',
    apellido: '',
    tipoUsuario: '',
    idUsuario: Number(localStorage.getItem('idUsuario')) || null,
    cuit: '',
    empid: localStorage.getItem('empid') || '',
    tieneWFCompra: null,
    idPerfil: null,
    aceptaTyC: null,
    aceptaNoHayDatosFiscales: 0,
    existeDatosFiscales: 0,
    emiteFacturaA: 0
  }))

  const programarRenovacion = (jwt) => {
    clearTimeout(timerRef.current)
    const tiempoParaRenovar = getTokenExpireMS(jwt)
    if (tiempoParaRenovar === null) return
    if (tiempoParaRenovar <= 0) {
      renovarRef.current?.()
      return
    }
    timerRef.current = setTimeout(() => {
      const inactivoMs = Date.now() - ultimaActividadRef.current
      if (inactivoMs <= VENTANA_INACTIVIDAD_MS) {
        renovarRef.current()
      } else {
        avisarInactividadRef.current()
      }
    }, tiempoParaRenovar)
  }

  const avisarInactividad = () => {
    setMostrarAvisoSesion(true)
    clearTimeout(graciaRef.current)
    graciaRef.current = setTimeout(() => {
      cerrarSesionPorInactividad()
    }, TIEMPO_GRACIA_CIERRE_MS)
  }

  const extenderSesion = () => {
    clearTimeout(graciaRef.current)
    setMostrarAvisoSesion(false)
    ultimaActividadRef.current = Date.now()
    renovarRef.current()
  }

  const cerrarSesionPorInactividad = () => {
    clearTimeout(graciaRef.current)
    setMostrarAvisoSesion(false)
    cerrarSesion(logout)
  }

  const guardarItem = (clave, valor) => {
    if (valor === null || valor === undefined) {
      localStorage.removeItem(clave)
      return
    }
    localStorage.setItem(clave, valor)
  }

  const aplicarSesion = (jwt, refreshToken, usuario, empid, idUsuario) => {
    setToken(jwt)
    setIsAuthenticated(true)
    guardarItem('jwt', jwt)
    guardarItem('refreshToken', refreshToken)
    guardarItem('usuario', usuario)
    guardarItem('empid', empid)
    guardarItem('idUsuario', idUsuario)
    setTokenGetter(() => jwt)
    programarRenovacion(jwt)
  }

  const buscarDatosUsuario = async (idUsuario) => {
    if (!idUsuario) {
      setCargandoUsuario(false)
      return
    }
    setCargandoUsuario(true)
    try {
      const { data } = await usuarioService.buscarPorId(parseInt(idUsuario))
      updateUserData({
        nombre: data.nombre,
        apellido: data.apellido,
        tipoUsuario: data.tipoUsuario,
        idUsuario: data.idUsuario,
        cuit: data.cuit,
        empid: data.empid,
        tieneWFCompra: data.tieneWFCompra ?? null,
        idPerfil: data.idPerfil ?? null,
        aceptaTyC: data.aceptaTyC ?? null,
        aceptaNoHayDatosFiscales: data.aceptaNoHayDatosFiscales ?? 0,
        existeDatosFiscales: data.existeDatosFiscales,
        emiteFacturaA: data.emiteFacturaA ?? 0
      })
    } catch (error) {
      toastErrorLog(error, 'Ocurrió un error al obtener los datos del usuario.', 'usuario')
    } finally {
      setCargandoUsuario(false)
    }
  }

  const updateUserData = useCallback((userData) => {
    setLoginDataContext((prevState) => ({ ...prevState, ...userData }))
  }, [])

  const logout = () => {
    clearTimeout(timerRef.current)
    clearTimeout(graciaRef.current)
    setMostrarAvisoSesion(false)
    setToken('')
    setIsAuthenticated(false)
    setCargandoUsuario(false)
    setLoginDataContext({
      nombre: '',
      apellido: '',
      tipoUsuario: '',
      idUsuario: null,
      cuit: '',
      empid: '',
      tieneWFCompra: null,
      idPerfil: null,
      aceptaTyC: null,
      aceptaNoHayDatosFiscales: 0,
      existeDatosFiscales: 0,
      emiteFacturaA: 0
    })
    localStorage.clear()
  }

  const renewToken = async () => {
    try {
      await renovarToken()
    } catch (error) {
      toastErrorLog(error, 'Ocurrió un error al renovar el token.', 'login')
    }
  }

  useEffect(() => {
    renovarRef.current = renewToken
    avisarInactividadRef.current = avisarInactividad
  })

  const procesarTokenUrl = async (tokenUrl) => {
    try {
      const decoded = decodeToken(tokenUrl)
      if (!decoded?.idUsuario) {
        setCargandoUsuario(false)
        return
      }
      localStorage.clear()
      setTokenGetter(() => null)
      const { data } = await loginService.validarUsuario({ token: decoded.token })
      if (!data?.resultado) {
        setCargandoUsuario(false)
        return
      }
      aplicarSesion(
        decoded.token,
        decoded.refreshToken,
        decoded.usuario,
        decoded.empid,
        decoded.idUsuario
      )
      await buscarDatosUsuario(decoded.idUsuario)
    } catch (error) {
      toastErrorLog(error, 'No se pudo iniciar la sesión.', 'login')
      setCargandoUsuario(false)
    }
  }

  useEffect(() => {
    const tokenUrl = new URLSearchParams(window.location.search).get('token')

    if (tokenUrl) {
      procesarTokenUrl(tokenUrl)
    } else {
      const storedToken = localStorage.getItem('jwt')
      const idUsuario = localStorage.getItem('idUsuario')

      if (storedToken && !tokenVencido(storedToken)) {
        setToken(storedToken)
        setIsAuthenticated(true)
        setTokenGetter(() => storedToken)
        programarRenovacion(storedToken)
        buscarDatosUsuario(idUsuario)
      } else {
        if (storedToken) {
          localStorage.clear()
        }
        setCargandoUsuario(false)
      }
    }

    setOnTokenRenovado((data) => {
      setToken(data.token)
      setIsAuthenticated(true)
      programarRenovacion(data.token)
    })
    setOnSesionExpirada(logout)

    const registrarActividad = () => {
      ultimaActividadRef.current = Date.now()
    }
    window.addEventListener('click', registrarActividad, { capture: true })
    window.addEventListener('keypress', registrarActividad, { capture: true })

    return () => {
      clearTimeout(timerRef.current)
      clearTimeout(graciaRef.current)
      window.removeEventListener('click', registrarActividad, { capture: true })
      window.removeEventListener('keypress', registrarActividad, { capture: true })
    }
  }, [])

  const esPerfilAdmin =
    loginDataContext.idPerfil === ID_PERFIL_ADMIN_EBP ||
    loginDataContext.idPerfil === ID_PERFIL_ADMIN_EMPRESA

  const esAdminEBP = loginDataContext.idPerfil === ID_PERFIL_ADMIN_EBP

  const setLoginData = (data) => {
    const { token: jwt, refreshToken, empid, usuario, idUsuario } = data
    if (!idUsuario) {
      toastErrorLog(
        new Error('Sin datos del usuario'),
        'No se pudieron obtener los datos de la sesión. Volvé a ingresar.',
        'login'
      )
      return
    }
    buscarDatosUsuario(idUsuario)
    aplicarSesion(jwt, refreshToken, usuario, empid, idUsuario)
  }

  return (
    <LoginContext.Provider
      value={{
        loginDataContext,
        esPerfilAdmin,
        esAdminEBP,
        token,
        isAuthenticated,
        cargandoUsuario,
        mostrarAvisoSesion,
        extenderSesion,
        cerrarSesionPorInactividad,
        logout,
        renewToken,
        updateUserData,
        setLoginData
      }}
    >
      {cargandoUsuario ? (
        <div className="d-flex justify-content-center align-items-center vh-100">
          <Spinner animation="border" role="status" aria-label="Cargando" />
        </div>
      ) : (
        props.children
      )}
    </LoginContext.Provider>
  )
}

LoginProvider.propTypes = {
  children: PropTypes.node.isRequired
}
